import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart,
  ClipboardList,
  MessageCircle,
  BarChart3,
  Scissors,
  Package,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Building2,
  FlaskConical,
  Microscope,
  FileText,
  Clock,
} from 'lucide-react';

// Logo: Huella de perro (ANIMAvet)
const PawPrint = ({ className, style, animated }: { className?: string; style?: React.CSSProperties; animated?: boolean }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className} style={style} xmlns="http://www.w3.org/2000/svg">
    {animated ? (
      <>
        <ellipse cx="16" cy="22" rx="7" ry="6">
          <animate attributeName="ry" values="6;5.5;6" dur="2s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="7" cy="12" rx="4" ry="4.5">
          <animate attributeName="cy" values="12;10;12" dur="1.5s" repeatCount="indefinite" begin="0s" />
        </ellipse>
        <ellipse cx="13" cy="6" rx="3.5" ry="4">
          <animate attributeName="cy" values="6;4.5;6" dur="1.5s" repeatCount="indefinite" begin="0.2s" />
        </ellipse>
        <ellipse cx="19" cy="6" rx="3.5" ry="4">
          <animate attributeName="cy" values="6;4.5;6" dur="1.5s" repeatCount="indefinite" begin="0.4s" />
        </ellipse>
        <ellipse cx="25" cy="12" rx="4" ry="4.5">
          <animate attributeName="cy" values="12;10;12" dur="1.5s" repeatCount="indefinite" begin="0.6s" />
        </ellipse>
      </>
    ) : (
      <>
        <ellipse cx="16" cy="22" rx="7" ry="6"/>
        <ellipse cx="7" cy="12" rx="4" ry="4.5"/>
        <ellipse cx="13" cy="6" rx="3.5" ry="4"/>
        <ellipse cx="19" cy="6" rx="3.5" ry="4"/>
        <ellipse cx="25" cy="12" rx="4" ry="4.5"/>
      </>
    )}
  </svg>
);

// Logo: Microscopio (ANIMAlab)
const LabLogo = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className} style={style} xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="26" width="16" height="3" rx="1" />
    <rect x="14" y="12" width="4" height="14" rx="1" />
    <ellipse cx="16" cy="8" rx="4" ry="3" />
    <circle cx="16" cy="20" r="3" />
    <rect x="10" y="22" width="12" height="2" rx="0.5" />
  </svg>
);

const vetFeatures = [
  { icon: Heart, title: 'Historias Clínicas', description: 'Fichas médicas con vacunas y adjuntos', color: 'bg-rose-100 text-rose-600' },
  { icon: Package, title: 'Control de Stock', description: 'Inventario con alertas de vencimiento', color: 'bg-blue-100 text-blue-600' },
  { icon: ClipboardList, title: 'Comandas', description: 'Sistema integrado con consultas', color: 'bg-purple-100 text-purple-600' },
  { icon: Scissors, title: 'Peluquería', description: 'Agenda de turnos con calendario', color: 'bg-pink-100 text-pink-600' },
  { icon: MessageCircle, title: 'Recordatorios', description: 'Envío de recordatorios WhatsApp', color: 'bg-green-100 text-green-600' },
  { icon: BarChart3, title: 'Reportes', description: 'Estadísticas y caja diaria', color: 'bg-amber-100 text-amber-600' },
];

const labFeatures = [
  { icon: FlaskConical, title: 'Muestras', description: 'Registro con trazabilidad completa', color: 'bg-cyan-100 text-cyan-600' },
  { icon: Microscope, title: 'Análisis', description: 'Catálogo de estudios y valores', color: 'bg-purple-100 text-purple-600' },
  { icon: FileText, title: 'Resultados', description: 'Informes PDF automáticos', color: 'bg-blue-100 text-blue-600' },
  { icon: Building2, title: 'Clientes', description: 'Gestión de veterinarias', color: 'bg-amber-100 text-amber-600' },
  { icon: Clock, title: 'Tiempos', description: 'Control de entregas', color: 'bg-rose-100 text-rose-600' },
  { icon: BarChart3, title: 'Estadísticas', description: 'Reportes de productividad', color: 'bg-green-100 text-green-600' },
];

const benefits = [
  'Multi-usuario con roles y permisos',
  'Funciona en cualquier dispositivo',
  'Datos seguros en la nube',
  'Soporte técnico incluido',
  'Actualizaciones automáticas',
  'Sin instalación requerida',
];

type ProductType = 'vet' | 'lab';

export default function Landing() {
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<ProductType>('vet');

  return (
    <div className="min-h-screen bg-cream-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-cream-50/80 backdrop-blur-lg border-b border-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-taupe-500 to-taupe-700 rounded-xl flex items-center justify-center shadow-md">
                  <PawPrint className="w-6 h-6 text-cream-100" />
                </div>
                <span className="text-xl font-bold text-taupe-800 tracking-tight">
                  <span>ANIMA</span>
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="text-taupe-600 hover:text-taupe-800 font-medium transition-colors"
            >
              Iniciar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-96 h-96 bg-taupe-200/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-cream-300/50 rounded-full blur-3xl -translate-y-1/2"></div>
          <PawPrint animated className="absolute w-20 h-20 text-taupe-200/25" style={{ top: '15%', left: '5%', transform: 'rotate(-15deg)' }} />
          <PawPrint animated className="absolute w-14 h-14 text-taupe-200/20" style={{ top: '25%', right: '8%', transform: 'rotate(20deg)' }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-taupe-100 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-taupe-600" />
              <span className="text-taupe-700 text-sm font-medium">Sistemas de Gestión Veterinaria</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-taupe-800 leading-tight">
              Soluciones digitales para{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-taupe-600 to-taupe-800">
                el mundo veterinario
              </span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-taupe-600 max-w-2xl mx-auto">
              Plataformas simples y completas para clínicas veterinarias y laboratorios de análisis.
              Diseñadas en Argentina, para Argentina.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#productos"
                className="inline-flex items-center gap-2 bg-taupe-700 hover:bg-taupe-800 text-cream-50 px-8 py-4 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all font-medium"
              >
                Ver productos
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="https://wa.me/5493815791046?text=Hola!%20Me%20interesa%20conocer%20los%20productos%20ANIMA"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-4 text-taupe-700 hover:text-taupe-800 font-medium transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Contactar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="productos" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-taupe-800">Nuestros productos</h2>
            <p className="mt-4 text-lg text-taupe-600">
              Elegí el que mejor se adapta a tu negocio
            </p>
          </div>

          {/* Product Toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-cream-100 rounded-xl p-1.5">
              <button
                onClick={() => setSelectedProduct('vet')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  selectedProduct === 'vet'
                    ? 'bg-white text-taupe-800 shadow-md'
                    : 'text-taupe-500 hover:text-taupe-700'
                }`}
              >
                <PawPrint className="w-5 h-5" />
                <span>ANIMA<span className="font-light">vet</span></span>
              </button>
              <button
                onClick={() => setSelectedProduct('lab')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  selectedProduct === 'lab'
                    ? 'bg-white text-cyan-700 shadow-md'
                    : 'text-taupe-500 hover:text-taupe-700'
                }`}
              >
                <LabLogo className="w-5 h-5" />
                <span>ANIMA<span className="font-light">lab</span></span>
              </button>
            </div>
          </div>

          {/* Product Content */}
          <div className="transition-all">
            {selectedProduct === 'vet' ? (
              <div>
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-taupe-500 to-taupe-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <PawPrint className="w-8 h-8 text-cream-100" />
                    </div>
                    <h3 className="text-2xl font-bold text-taupe-800">
                      ANIMA<span className="font-light text-taupe-500">vet</span>
                    </h3>
                  </div>
                  <p className="text-taupe-600 max-w-2xl mx-auto">
                    Sistema integral para clínicas veterinarias. Gestiona pacientes, inventario, ventas, peluquería y más.
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {vetFeatures.map((feature, index) => (
                    <div
                      key={index}
                      className="p-4 rounded-xl bg-cream-50 border border-cream-200 hover:shadow-md hover:border-taupe-300 transition-all text-center"
                    >
                      <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <h4 className="font-semibold text-taupe-800 text-sm mb-1">{feature.title}</h4>
                      <p className="text-xs text-taupe-500">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <LabLogo className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800">
                      ANIMA<span className="font-light text-cyan-600">lab</span>
                    </h3>
                  </div>
                  <p className="text-slate-600 max-w-2xl mx-auto">
                    Sistema para laboratorios de análisis veterinarios. Controla muestras, análisis y resultados en una sola plataforma.
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {labFeatures.map((feature, index) => (
                    <div
                      key={index}
                      className="p-4 rounded-xl bg-slate-50 border border-slate-200 hover:shadow-md hover:border-cyan-300 transition-all text-center"
                    >
                      <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <h4 className="font-semibold text-slate-800 text-sm mb-1">{feature.title}</h4>
                      <p className="text-xs text-slate-500">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-taupe-700 via-taupe-800 to-taupe-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cream-400/10 rounded-full blur-3xl"></div>
          <PawPrint animated className="absolute w-24 h-24 text-cream-200/10" style={{ top: '10%', right: '10%', transform: 'rotate(15deg)' }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-cream-50 mb-6">
                ¿Por qué elegirnos?
              </h2>
              <p className="text-taupe-200 text-lg mb-8">
                Nuestros sistemas fueron creados pensando en las necesidades específicas del sector veterinario argentino.
              </p>

              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    </div>
                    <span className="text-cream-100">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-cream-50/10 backdrop-blur-sm rounded-2xl p-6 border border-cream-200/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-taupe-400 to-taupe-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <PawPrint className="w-9 h-9 text-cream-100" />
                </div>
                <h4 className="text-lg font-bold text-cream-50">ANIMAvet</h4>
                <p className="text-sm text-taupe-300 mt-1">Clínicas veterinarias</p>
              </div>
              <div className="bg-cyan-600/20 backdrop-blur-sm rounded-2xl p-6 border border-cyan-400/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <LabLogo className="w-9 h-9 text-white" />
                </div>
                <h4 className="text-lg font-bold text-white">ANIMAlab</h4>
                <p className="text-sm text-cyan-200 mt-1">Laboratorios</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contacto" className="py-16 bg-white border-t border-cream-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-taupe-800 mb-4">
            ¿Tenés dudas?
          </h2>
          <p className="text-taupe-600 mb-8">
            Escribinos por WhatsApp y te respondemos a la brevedad
          </p>

          <a
            href="https://wa.me/5493815791046?text=Hola!%20Quiero%20m%C3%A1s%20informaci%C3%B3n%20sobre%20ANIMAvet"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all shadow-lg hover:shadow-xl"
          >
            <MessageCircle className="w-6 h-6" />
            Contactar por WhatsApp
          </a>

          <p className="text-sm text-taupe-500 mt-6">
            +54 9 381 579 1046
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-taupe-900 text-cream-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-taupe-700 rounded-lg flex items-center justify-center">
                  <PawPrint className="w-5 h-5 text-cream-300" />
                </div>
                <span className="font-bold text-cream-50">ANIMA<span className="font-light text-cream-400">vet</span></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-cyan-700 rounded-lg flex items-center justify-center">
                  <LabLogo className="w-5 h-5 text-cyan-300" />
                </div>
                <span className="font-bold text-cream-50">ANIMA<span className="font-light text-cyan-400">lab</span></span>
              </div>
            </div>

            <a
              href="https://wa.me/5493815791046"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-cream-50 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>

            <p className="text-sm text-taupe-400">© 2025 ANIMA. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
